/**
 * LandingCmsPage — superadmin CMS for the public landing page.
 *
 * Sits at /superadmin/landing-cms.  Composed of:
 *   - Sticky header with Save Draft / Publish / Discard buttons + dirty flag
 *   - Top controls: language-switch toggle + hero-mode segmented control
 *     (both settings save instantly, not gated behind Publish)
 *   - 10 section panels (Brand / Navbar / Hero / Features / Advantages /
 *     Why Choose / Testimonials / FAQ / CTA / Contact / Footer)
 *
 * Each section panel uses the bilingual-field primitives from ./landing-cms/
 * fields/BilingualFields.jsx and writes through the useLandingDraft hook
 * via dotted-path mutations like `setField('en', 'hero.slides.0.title', 'X')`.
 *
 * The design of the public landing page itself is unchanged.  This CMS only
 * controls the CONTENT that LandingPage.jsx renders.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiOutlineArrowLeft, HiOutlineClock, HiOutlineEye,
  HiOutlineCheckCircle, HiOutlineExclamationTriangle,
  HiOutlineArrowPath, HiOutlineTrash, HiOutlineDocumentArrowUp,
} from 'react-icons/hi2'

import { useLandingDraft } from './landing-cms/useLandingDraft'
import ImportJsonModal from './landing-cms/ImportJsonModal'

import BrandSection from './landing-cms/sections/BrandSection'
import NavbarSection from './landing-cms/sections/NavbarSection'
import HeroSection from './landing-cms/sections/HeroSection'
import FeaturesSection from './landing-cms/sections/FeaturesSection'
import AdvantagesSection from './landing-cms/sections/AdvantagesSection'
import WhyChooseSection from './landing-cms/sections/WhyChooseSection'
import TestimonialsSection from './landing-cms/sections/TestimonialsSection'
import FaqSection from './landing-cms/sections/FaqSection'
import CtaSection from './landing-cms/sections/CtaSection'
import ContactSection from './landing-cms/sections/ContactSection'
import FooterSection from './landing-cms/sections/FooterSection'

function FlashMessage({ tone, children, onDismiss }) {
  const tones = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error:   'bg-red-50 border-red-200 text-red-800',
    info:    'bg-blue-50 border-blue-200 text-blue-800',
  }
  const Icon = tone === 'success' ? HiOutlineCheckCircle : HiOutlineExclamationTriangle
  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${tones[tone] || tones.info} mb-4`}>
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">{children}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-xs opacity-70 hover:opacity-100">Dismiss</button>
      )}
    </div>
  )
}

export default function LandingCmsPage() {
  const navigate = useNavigate()
  const draft = useLandingDraft()
  const [flash, setFlash] = useState(null)
  const [publishNote, setPublishNote] = useState('')
  const [confirmPublish, setConfirmPublish] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [importing, setImporting] = useState(false)

  const {
    loading, loadError,
    draftEn, draftAr,
    settings,
    setField, getField, setSubtree,
    updateSettings,
    saveDraft, publish, discardDraft, importPayload,
    savingDraft, publishing, savingSettings,
    isDirty, hasUnpublishedChanges,
  } = draft

  // -----------------------------------------------------------------
  // Action handlers
  // -----------------------------------------------------------------
  const handleSaveDraft = async () => {
    const res = await saveDraft()
    if (res.ok) {
      setFlash({ tone: 'success', message: 'Draft saved. Visitors don\'t see changes until you publish.' })
    } else {
      setFlash({ tone: 'error', message: res.error })
    }
  }

  const handlePublish = async () => {
    const res = await publish(publishNote)
    setConfirmPublish(false)
    setPublishNote('')
    if (res.ok) {
      setFlash({ tone: 'success', message: 'Published. The public landing page now reflects your changes.' })
    } else {
      setFlash({ tone: 'error', message: res.error })
    }
  }

  const handleDiscard = async () => {
    const res = await discardDraft()
    setConfirmDiscard(false)
    if (res.ok) {
      setFlash({ tone: 'info', message: 'Draft discarded. Editor now matches what visitors currently see.' })
    } else {
      setFlash({ tone: 'error', message: res.error })
    }
  }

  const handlePreview = () => {
    window.open('/?preview=draft', '_blank', 'noopener')
  }

  const handleImport = async (lang, payload) => {
    setImporting(true)
    try {
      const res = await importPayload(lang, payload)
      if (res.ok) {
        setFlash({
          tone: 'success',
          message: `Imported ${lang === 'en' ? 'English' : 'Arabic'} JSON into draft. Click Publish to make it live.`,
        })
      }
      return res
    } finally {
      setImporting(false)
    }
  }

  // -----------------------------------------------------------------
  // Loading & error states
  // -----------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
        Loading Landing CMS…
      </div>
    )
  }
  if (loadError) {
    return (
      <FlashMessage tone="error">{loadError}</FlashMessage>
    )
  }

  // -----------------------------------------------------------------
  // Shared props passed to every section editor
  // -----------------------------------------------------------------
  const sectionProps = { getField, setField, setSubtree, draftEn, draftAr }

  return (
    <div className="space-y-4">
      {/*
        Sticky header — single-row compact layout, total height capped at ~64px
        (well under the 80px budget).  Left side: back arrow + title +
        unpublished pill + language-switch micro toggle.  Right side: action
        buttons (Preview / Import / Discard / Save / Publish).  On very narrow
        viewports the right cluster wraps below — only happens under ~640px.
      */}
      <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur -mx-4 sm:-mx-6 px-4 sm:px-6 border-b border-gray-200">
        <div className="flex items-center gap-2 sm:gap-3 py-2 min-h-[56px] max-h-[80px] overflow-hidden">
          {/* Left cluster: back, title, dirty pill, language toggle */}
          <button
            onClick={() => navigate('/superadmin')}
            className="p-1.5 -ml-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-600 flex-shrink-0"
            aria-label="Back to superadmin"
          >
            <HiOutlineArrowLeft className="w-5 h-5" />
          </button>

          <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate min-w-0">
            Landing Page
          </h1>

          {hasUnpublishedChanges && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium flex-shrink-0">
              <HiOutlineClock className="w-3 h-3" />
              Unpublished
            </span>
          )}

          {/* Compact language-switch toggle — visible on md+ so the header
              doesn't break on phones.  Use the section panel below as the
              fallback control for tiny screens (none built — but the toggle
              is also accessible via the Hero section's mode block, indirectly). */}
          <label
            className="hidden md:inline-flex items-center gap-2 ms-1 px-2 py-1 rounded-md border border-gray-200 bg-white text-xs text-gray-700 cursor-pointer hover:bg-gray-50"
            title="When off, the EN/AR button is hidden from the public navbar"
          >
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.language_switch_enabled}
              onChange={(e) => updateSettings({ language_switch_enabled: e.target.checked })}
              disabled={savingSettings}
            />
            <span className="relative inline-block w-8 h-4 bg-gray-300 peer-checked:bg-cyan-600 rounded-full transition-colors">
              <span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${settings.language_switch_enabled ? 'translate-x-4' : ''}`} />
            </span>
            <span className="whitespace-nowrap">EN / AR toggle</span>
          </label>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right cluster: action buttons */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={handlePreview}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
              title="Preview"
            >
              <HiOutlineEye className="w-4 h-4" />
              <span className="hidden sm:inline">Preview</span>
            </button>
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
              title="Import JSON content"
            >
              <HiOutlineDocumentArrowUp className="w-4 h-4" />
              <span className="hidden sm:inline">Import</span>
            </button>
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              disabled={!hasUnpublishedChanges}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Discard draft"
            >
              <HiOutlineTrash className="w-4 h-4" />
              <span className="hidden sm:inline">Discard</span>
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={!isDirty || savingDraft}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-cyan-300 bg-cyan-50 text-xs sm:text-sm text-cyan-800 hover:bg-cyan-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
            >
              {savingDraft ? 'Saving…' : 'Save draft'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmPublish(true)}
              disabled={!hasUnpublishedChanges || publishing}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-cyan-600 text-white text-xs sm:text-sm font-medium hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Flash messages */}
      {flash && (
        <FlashMessage tone={flash.tone} onDismiss={() => setFlash(null)}>
          {flash.message}
        </FlashMessage>
      )}

      {/* Confirm dialogs (inline modals) */}
      {confirmPublish && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setConfirmPublish(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Publish to public landing page?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Both English and Arabic content will become live immediately. Visitors will see your changes.
            </p>
            <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">Optional note (saved in history)</label>
            <input
              type="text"
              value={publishNote}
              onChange={(e) => setPublishNote(e.target.value)}
              placeholder='e.g. "Updated hero copy + new testimonial"'
              maxLength={200}
              className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmPublish(false)} className="px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handlePublish} disabled={publishing} className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 disabled:opacity-50">
                {publishing ? 'Publishing…' : 'Publish now'}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDiscard && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setConfirmDiscard(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Discard all draft edits?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Your in-progress edits across both languages will be erased and replaced with whatever is currently live.
              This cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDiscard(false)} className="px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100">Keep editing</button>
              <button onClick={handleDiscard} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">
                <HiOutlineArrowPath className="inline w-4 h-4 me-1" />
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Optional JSON import modal — opens from the Import JSON button. */}
      <ImportJsonModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
        importing={importing}
      />

      {/* Section panels — page order matches the public landing page top→bottom */}
      <BrandSection {...sectionProps} />
      <NavbarSection {...sectionProps} />
      <HeroSection
        {...sectionProps}
        settings={settings}
        updateSettings={updateSettings}
        savingSettings={savingSettings}
      />
      <FeaturesSection {...sectionProps} />
      <AdvantagesSection {...sectionProps} />
      <WhyChooseSection {...sectionProps} />
      <TestimonialsSection {...sectionProps} />
      <FaqSection {...sectionProps} />
      <CtaSection {...sectionProps} />
      <ContactSection {...sectionProps} />
      <FooterSection {...sectionProps} />
    </div>
  )
}
